import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { patchCommerceLink } from 'redux/commerce/actions';
import { setLastPatchedId } from 'redux/common/actions';

import { Link } from 'react-router-dom';

import CommercePaymentProviderDropdown
  from 'components/dashboard/commerce-content/form-elements/CommercePaymentProviderDropdown';
import CommerceCheckbox
  from 'components/dashboard/commerce-content/form-elements/CommerceCheckbox';
import CommerceTextArea
  from 'components/dashboard/commerce-content/form-elements/CommerceTextArea';
import CommonInputField
  from 'components/elements/inputs/CommonInputField';
import CommerceDonationAmounts
  from 'components/dashboard/commerce-content/commerce-link/CommerceDonationAmounts';
import CommerceButtonColorSelector
  from 'components/dashboard/commerce-content/form-elements/CommerceButtonColorSelector';

import { priceValidation } from 'utils/validations/price';
import { percentValidation } from 'utils/validations/percent';

import { modifyPrice } from 'utils/common';

import './CommerceItemForm.css';

function CommerceItemForm(props) {
  const {
    item,
    patchCommerceLink,
    patchCommerceLinkError,
    lastPatchedId,
    setLastPatchedId,
    me,
  } = props;

  return (
    <div className="commerce-item-form__component">
      {/* Info Message */}
      {!item.is_published &&
        <div className="info-message">
          (!) Form is not active yet. Once you fill all the required
          details, please activate it using the switcher
        </div>
      }

      {/* Payment Provider */}
      <CommercePaymentProviderDropdown linkItem={item}/>

      {/* Description */}
      <CommerceTextArea
        key={`commerce-donation-form-description-key-${item.id}`}
        label="Description (optional)"
        rows={4}
        maxLength={200}
        placeholder="Leave description here..."
        initialValue={item.description}
        parameter="description"
        patchFunction={patchCommerceLink}
        linkId={item.id}
      />

      {/* Commerce Donation Amounts */}
      <CommerceDonationAmounts linkItem={item} />

      {/* Allow a custom amount to be entered */}
      {item.link_type === 'donation' &&
        <CommerceCheckbox
          key={`commerce-donation-is-custom-amount-checkbox-key-${item.id}`}
          linkId={item.id}
          initialValue={item.is_custom_amount}
          parameter="is_custom_amount"
          patchFunction={patchCommerceLink}
          label="Allow a custom amount to be entered"
        />
      }

      {/* Set donation goal */}
      <CommerceCheckbox
        key={`commerce-donation-is-donation-goal-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_donation_goal}
        parameter="is_donation_goal"
        patchFunction={patchCommerceLink}
        label="Set donation goal"
      />

      {item.is_donation_goal &&
        <div className="field-wrapper">
          {item.payment_provider && item.currency &&
            <div className="prefix">{item.currency.symbol}</div>
          }
          <div className="input-wrapper">
            <CommonInputField
              key={`commerce-donation-goal-key-${item.id}`}
              initialValue={item.donation_goal ? modifyPrice(item.donation_goal) : ''}
              executedFunction={value => {
                patchCommerceLink(item.id, { donation_goal: modifyPrice(value) });
                setLastPatchedId(item.id);
              }}
              serverErrors={lastPatchedId === item.id &&
                patchCommerceLinkError && patchCommerceLinkError.donation_goal}
              validationFunction={priceValidation}
            />
          </div>
        </div>
      }

      {/* Do you need to include tax? */}
      <CommerceCheckbox
        key={`commerce-donation-is-tax-include-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_tax_include}
        parameter="is_tax_include"
        patchFunction={patchCommerceLink}
        label="Do you need to include tax?"
      />

      {item.is_tax_include &&
        <div className="field-wrapper tax">
          <div className="field-prefix">
            Tax rate (inclusive)
          </div>
          <div className="input-wrapper">
            <CommonInputField
              key={`commerce-tax-rate-key-${item.id}`}
              initialValue={item.tax_rate ? modifyPrice(item.tax_rate) : ''}
              executedFunction={value => {
                patchCommerceLink(item.id, { tax_rate: modifyPrice(value) });
                setLastPatchedId(item.id);
              }}
              serverErrors={lastPatchedId === item.id &&
                patchCommerceLinkError && patchCommerceLinkError.tax_rate}
              validationFunction={percentValidation}
            />
          </div>
          <div className="postfix">%</div>
        </div>
      }

      {/* Allow purchaser to enter message */}
      <CommerceCheckbox
        key={`commerce-donation-is-purchaser-message-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_purchaser_message}
        parameter="is_purchaser_message"
        patchFunction={patchCommerceLink}
        label="Allow purchaser to enter message"
      />

      {/* Purchaser needs to provide mobile phone */}
      <CommerceCheckbox
        key={`commerce-donation-is-purchaser-phone-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_purchaser_phone}
        parameter="is_purchaser_phone"
        patchFunction={patchCommerceLink}
        label="Purchaser needs to provide mobile phone"
      />

      {/* Purchaser needs to provide WhatsApp */}
      <CommerceCheckbox
        key={`commerce-donation-is-purchaser-whatsapp-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_purchaser_whatsapp}
        parameter="is_purchaser_whatsapp"
        patchFunction={patchCommerceLink}
        label="Purchaser needs to provide WhatsApp"
      />

      {/* Thank you message */}
      <CommonInputField
        key={`commerce-donation-form-thank_you_message-key-${item.id}`}
        label="Thank you message (optional)"
        initialValue={item.thank_you_message}
        executedFunction={value => {
          patchCommerceLink(item.id, { thank_you_message: value });
          setLastPatchedId(item.id);
        }}
        serverErrors={lastPatchedId === item.id &&
          patchCommerceLinkError && patchCommerceLinkError.thank_you_message}
        maxLength={30}
        placeholder="Leave message here..."
      />

      {/* Custom Button Color */}
      <CommerceCheckbox
        key={`commerce-donation-is-button-color-checkbox-key-${item.id}`}
        linkId={item.id}
        initialValue={item.is_button_color}
        parameter="is_button_color"
        patchFunction={patchCommerceLink}
        label="Custom Button Color"
        proLabel
        disabled={me.subscription && !me.subscription.ff_commerce_button_color}
      />

      {me.subscription && !me.subscription.ff_commerce_button_color &&
        <div className="locked-text">
          Custom Button Color is available on the PRO plan.
          You can upgrade <Link to="/billing">here</Link>
        </div>
      }

      {item.is_button_color &&
        <CommerceButtonColorSelector
          key={`commerce-button-color-selector-key-${item.id}`}
          pikerId={`commerce-button-color-selector-picker-id-${item.id}`}
          linkId={item.id}
          parameter="button_color"
          patchFunction={patchCommerceLink}
          initialColor={item.button_color}
        />
      }
    </div>
  );
}

CommerceItemForm.propTypes = {
  patchCommerceLink: PropTypes.func.isRequired,
  setLastPatchedId: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  patchCommerceLinkError: PropTypes.object,
  lastPatchedId: PropTypes.number,
  me: PropTypes.object,
};

CommerceItemForm.defaultProps = {
  patchCommerceLinkError: null,
  lastPatchedId: null,
  me: null,
};

const mapStateToProps = state => (
  {
    me: state.usersReducer.me,
    patchCommerceLinkError: state.commerceReducer.patchCommerceLinkError,
    lastPatchedId: state.commonReducer.lastPatchedId,
  }
);

export default connect(
  mapStateToProps,
  {
    patchCommerceLink,
    setLastPatchedId,
  },
)(CommerceItemForm);
